import { useState, useEffect } from 'react';
import Drawer from 'components/Layout/Drawer';
import { BusinessAddress } from 'models/business';
import { useCommands } from 'hooks/useCommands';
import NotificationMessage, { NotificationStyles } from 'components/Notification';
import ErrorModal from 'components/Modals/ErrorModal';
import { useParams } from 'react-router-dom';
import { useStates } from 'api/queries/useStates';
import { v4 as newGuid } from 'uuid';

import { AddBusinessAddress } from 'commands/business/addresses/AddBusinessAddress';
import { DeleteBusinessAddress } from 'commands/business/addresses/DeleteBusinessAddress';
import { ChangeAddressVisibility } from 'commands/business/addresses/ChangeAddressVisibility';
import { ChangeAddressCity } from 'commands/business/addresses/ChangeAddressCity';
import { ChangeAddressDirections } from 'commands/business/addresses/ChangeAddressDirections';
import { ChangeAddressDisplayName } from 'commands/business/addresses/ChangeAddressDisplayName';
import { ChangeAddressLine } from 'commands/business/addresses/ChangeAddressLine';
import { ChangeAddressOfficeEmail } from 'commands/business/addresses/ChangeAddressOfficeEmail';
import { ChangeAddressOfficePhone } from 'commands/business/addresses/ChangeAddressOfficePhone';
import { ChangeAddressPostcode } from 'commands/business/addresses/ChangeAddressPostcode';
import { ChangeAddressState } from 'commands/business/addresses/ChangeAddressState';

import EyeSvg from 'components/Svg/EyeSvg';
import TrashSvg from 'components/Svg/TrashSvg';
import SaveSvg from 'components/Svg/SaveSvg';

import DialogModal from 'components/Modals/DialogModal';
import AddressInput from './AddressInput';
import AddressSelect from './AddressSelect';

type Props = {
  toggleHandler: () => void;
  selectedAddress?: BusinessAddress;
  onSave: (addr: BusinessAddress) => void;
  onDelete?: (addrId: string) => void;
  mode?: 'add' | 'edit';
  addressesCount?: number;
};

const requiredFields = ['displayName', 'addressLine1', 'city', 'state', 'postalCode'] as const;

const validators: Record<string, (val: string) => string | null> = {
phoneNumber: v => (!v || /^[0-9+\-\s]+$/.test(v) ? null : 'Phone number is invalid'),
  officeEmail: v => (!v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? null : 'Invalid email'),
  postalCode: v => (/^[0-9]+$/.test(v) ? null : 'Postcode must be numeric'),
  directions: v => (!v || isValidUrl(v) ? null : 'Directions must be a valid URL'),
  state: v => (v === 'Please select' ? 'Please select a valid state' : null)
};

const isValidUrl = (str: string) => {
  try {
    new URL(str);
    return true;
  } catch {
    return false;
  }
};

const getInitialFormData = (addr?: BusinessAddress) => ({
  id: addr?.id ?? '',
  displayName: addr?.displayName ?? '',
  addressLine1: addr?.addressLine1 ?? '',
  addressLine2: addr?.addressLine2 ?? '',
  city: addr?.city ?? '',
  state: addr?.state ?? 'Please select',
  postalCode: addr?.postalCode ?? '',
  directions: addr?.directions ?? '',
  officeEmail: addr?.officeEmail ?? '',
  phoneNumber: addr?.phoneNumber ?? ''
});

export default function BusinessAddressEditDrawer({ toggleHandler, selectedAddress, onSave, onDelete, mode, addressesCount }: Props) {
  const { commands, showError, showSuccess, errorMessage, setShowSuccess } = useCommands();
  const { businessName } = useParams<{ businessName: string }>();
  const { data: states } = useStates(businessName);

  const [formData, setFormData] = useState(getInitialFormData(selectedAddress));
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [isVisible, setIsVisible] = useState<boolean>(selectedAddress?.isVisible ?? false);
  const [del, setDel] = useState(false);

  useEffect(() => {
    if (selectedAddress) {
      setFormData(getInitialFormData(selectedAddress));
      setIsVisible(selectedAddress.isVisible ?? false);
    }
  }, [selectedAddress]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (validators[field]) {
      setErrors(prev => ({ ...prev, [field]: validators[field](value) }));
    }
  };

  const isFormValid = () => requiredFields.every(f => formData[f].trim()) && Object.values(errors).every(err => !err);

  const handleSave = () => {
    const newErrors: Record<string, string | null> = {};
    for (const field of requiredFields) {
      if (!formData[field].trim()) newErrors[field] = `${field} is required`;
    }
    for (const [field, validate] of Object.entries(validators)) {
      newErrors[field] = validate(formData[field as keyof typeof formData] ?? '') || newErrors[field];
    }
    setErrors(newErrors);

    if (Object.values(newErrors).some(Boolean)) return;

    const updatedAddress: BusinessAddress = {
      ...formData,
      id: selectedAddress?.id ?? newGuid(),
      isVisible
    };

    if (mode === 'edit') {
      const addIfChanged = (Cmd: any, newVal: string, oldVal?: string) => {
        if (newVal.trim() !== (oldVal ?? '').trim()) {
          commands.add(new Cmd(newVal, selectedAddress!.id));
        }
      };

      addIfChanged(ChangeAddressDisplayName, formData.displayName, selectedAddress?.displayName);
      addIfChanged(ChangeAddressLine, formData.addressLine1, selectedAddress?.addressLine1);
      addIfChanged(ChangeAddressLine, formData.addressLine2, selectedAddress?.addressLine2);
      addIfChanged(ChangeAddressCity, formData.city, selectedAddress?.city);
      addIfChanged(ChangeAddressState, formData.state, selectedAddress?.state);
      addIfChanged(ChangeAddressPostcode, formData.postalCode, selectedAddress?.postalCode);
      addIfChanged(ChangeAddressDirections, formData.directions, selectedAddress?.directions);
      addIfChanged(ChangeAddressOfficeEmail, formData.officeEmail, selectedAddress?.officeEmail);
      addIfChanged(ChangeAddressOfficePhone, formData.phoneNumber, selectedAddress?.phoneNumber);

      commands.send();
    } else {
      if (addressesCount && addressesCount >= 9) {
        setErrors({ ...errors, form: 'You can only create up to 9 addresses.' });
        return;
      }

      commands.add(new AddBusinessAddress({ ...updatedAddress, sortOrder: addressesCount ?? 0 }));
      commands.send().then(() => window.location.reload());
    }

    setShowSuccess(true);
    onSave(updatedAddress);
  };

  const handleDelete = () => {
    if (!selectedAddress) return;
    commands.add(new DeleteBusinessAddress(selectedAddress.id)).send();
    onDelete?.(selectedAddress.id);
    toggleHandler();
  };

  const handleToggleVisibility = () => {
    const newVis = !isVisible;
    setIsVisible(newVis);
    if (mode === 'edit' && selectedAddress) {
      commands.add(new ChangeAddressVisibility(newVis, selectedAddress.id));
    }
  };

  return (
    <Drawer
      toggleHandler={toggleHandler}
      title={mode === 'add' ? 'Add Address' : `Edit ${selectedAddress?.displayName}`}
      titleColour='text-blue-lm'
      titleSize='text-2xl'
      actions={
        <div className='flex items-center justify-between'>
          <div className='flex items-center space-x-2'>
            <button className='text-blue-crm text-xl' onClick={handleToggleVisibility}>
              <EyeSvg isHidden={!isVisible} className='w-5 h-5' />
            </button>
            {mode === 'edit' && (
              <button className='text-red-500 text-xl' onClick={() => setDel(true)}>
                <TrashSvg />
              </button>
            )}
          </div>
          <button
            onClick={handleSave}
            disabled={!isFormValid()}
            className={`flex items-center py-2 px-4 rounded-md text-white font-medium text-xs ${
              isFormValid() ? 'bg-blue-crm' : 'bg-gray-400 cursor-not-allowed'
            }`}
          >
            <span className='mr-2'>
              <SaveSvg />
            </span>
            Save
          </button>
        </div>
      }
    >
      <ErrorModal open={showError} error={null} errorMessage={errorMessage} />
      <NotificationMessage
        message='Address saved successfully'
        type={NotificationStyles.Happy}
        show={!showError && showSuccess}
        setShow={setShowSuccess}
        drawer
      />

      {del && (
        <DialogModal
          open={del}
          title='Warning - You are deleting an Address'
          actionText='Delete Address'
          closeModal={() => setDel(false)}
          description='Are you sure you want to delete this address? This action is permanent and cannot be undone.'
          submitHandler={handleDelete}
          isRed
        />
      )}

      <div className='mt-6 space-y-6'>
        <AddressInput
          label='Business Display Name *'
          value={formData.displayName}
          onChange={e => handleInputChange('displayName', e.target.value)}
          error={errors.displayName || ''}
        />
        <AddressInput
          label='Address line 1 *'
          value={formData.addressLine1}
          onChange={e => handleInputChange('addressLine1', e.target.value)}
          error={errors.addressLine1 || ''}
        />
        <AddressInput label='Address line 2' value={formData.addressLine2} onChange={e => handleInputChange('addressLine2', e.target.value)} />
        <AddressInput label='City *' value={formData.city} onChange={e => handleInputChange('city', e.target.value)} error={errors.city || ''} />
        <AddressSelect
          label='State *'
          value={formData.state}
          onChange={e => handleInputChange('state', e.target.value)}
          options={states ?? []}
          error={errors.state || ''}
        />
        <AddressInput
          label='Postcode *'
          value={formData.postalCode}
          onChange={e => handleInputChange('postalCode', e.target.value)}
          error={errors.postalCode || ''}
        />
        <AddressInput
          label='Directions'
          value={formData.directions}
          onChange={e => handleInputChange('directions', e.target.value)}
          error={errors.directions || ''}
        />
        <AddressInput
          label='Office Email'
          value={formData.officeEmail}
          onChange={e => handleInputChange('officeEmail', e.target.value)}
          error={errors.officeEmail || ''}
        />
        <AddressInput
          label='Phone Number'
          value={formData.phoneNumber}
          onChange={e => handleInputChange('phoneNumber', e.target.value)}
          error={errors.phoneNumber || ''}
        />
        {errors.form && <p className='text-red-600 text-sm'>{errors.form}</p>}
      </div>
    </Drawer>
  );
}
